import { useState } from 'react';
import CatalogPromotionModal from './CatalogPromotionModal';
import { canPromoteToCatalog } from '../../lib/permissions';

// Issue #422, Session 78s.1. Gate decision M16 — Screen 5.
//
// In plain terms: the section on a vendor's page that lists what THEY sell, as
// opposed to what we buy from them. Grimco's price list might be four hundred
// products and D&R might stock nine of them; the other three hundred and
// ninety-one live here, so that when somebody asks for one the part number and
// the price are already known.
//
// WITHOUT THIS SECTION THE IMPORT WRITES INTO THE DARK, which is M16's own
// argument and it turned out to be worse than the gate knew. A row with no
// item was not invisible — it rendered in "Items supplied" as an em dash with a
// price beside it, because that table reads `inventory_items.name` and there is
// no item to read. So importing four hundred rows would have produced four
// hundred nameless rows in the one table on the page, and the fix is not only
// to add this section but to take those rows OUT of that one.
//
// M21. Fully mobile, deliberately, and it is one of only two steps in this
// whole feature that are — looking up what a vendor sells is something Jennifer
// does standing at a counter with a supplier on the phone. Choosing a
// four-hundred-row spreadsheet is not.

const ACCENT = '#1D9E75';

function money(n) {
  const v = parseFloat(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '—';
}

// What one of these costs per unit — the number every other screen reasons in.
// Postgres computes it as a generated column, but a freshly built row in memory
// has not been back from the database yet, so it is derived here too rather
// than shown as blank.
function eachCost(row) {
  const price = parseFloat(row.vendor_price);
  const qty = parseInt(row.pack_qty, 10);
  if (!Number.isFinite(price) || !Number.isFinite(qty) || qty <= 0) return null;
  return price / qty;
}

function packLabel(row) {
  const qty = parseInt(row.pack_qty, 10) || 1;
  const unit = row.pack_unit || 'pack';
  return qty === 1 ? unit : `${qty} per ${unit}`;
}

function CatalogRow({ row, isMobile, canPromote, onPromote }) {
  const each = eachCost(row);
  return (
    <div style={{
      display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 4 : 10,
      padding: '9px 11px', marginBottom: 6, borderRadius: 7,
      border: '1px solid #E8E8E8', background: '#fff',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>
          {row.off_catalog_description || '(no description)'}
        </div>
        <div style={{ fontSize: 11.5, color: '#7A7A7A', marginTop: 2 }}>
          {row.vendor_item_number || '(no part number)'} · {packLabel(row)}
          {/* A row born from an off-catalog purchase is a record of something
              that was actually bought, not a line from a price list. Marked,
              because the two look identical otherwise and only one of them is
              evidence. */}
          {row.is_provisional && ' · from an order'}
        </div>
      </div>

      <div style={{
        textAlign: isMobile ? 'left' : 'right',
        minWidth: isMobile ? 0 : 130, flexShrink: 0,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>
          {money(row.vendor_price)}
        </div>
        <div style={{ fontSize: 11, color: '#7A7A7A' }}>
          {each == null ? '—' : `$${each.toFixed(2)} each`}
        </div>
      </div>

      {/* M16's action, and M17 decides who sees it: turning one of a vendor's
          products into something D&R stocks is a decision about the catalog,
          so it runs through the same promote path and the same permission it
          always has. Jennifer loads the shelf; Angie decides what comes off it. */}
      {canPromote && (
        <button
          onClick={() => onPromote(row)}
          style={{
            flexShrink: 0, width: isMobile ? '100%' : 'auto',
            marginTop: isMobile ? 6 : 0,
            padding: '6px 12px', background: '#fff', color: ACCENT,
            border: `1px solid ${ACCENT}`, borderRadius: 6,
            fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Add to our catalog
        </button>
      )}
    </div>
  );
}

export default function VendorOwnCatalogSection({
  vendor, rows, isMobile, auth, onChanged,
}) {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [promoting, setPromoting] = useState(null);

  const canPromote = canPromoteToCatalog(auth);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
      String(r.off_catalog_description || '').toLowerCase().includes(q)
      || String(r.vendor_item_number || '').toLowerCase().includes(q))
    : rows;

  // A CEILING RATHER THAN A SCROLLING BOX OF FOUR HUNDRED. The realistic size
  // of an imported catalog is hundreds of rows, and rendering all of them puts
  // the buttons underneath this section below a fold nobody reaches — which is
  // #470 and #432's lesson, met again on a list this feature is what makes
  // long. Search is the way through a big catalog; the list is the way through
  // a small one.
  const CEILING = 25;
  const shown = showAll ? filtered : filtered.slice(0, CEILING);
  const hidden = filtered.length - shown.length;

  if (!rows.length) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginBottom: 10,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#7A7A7A',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          Their catalog
        </div>
        <div style={{ fontSize: 11, color: '#9A9A9A' }}>
          {rows.length} {rows.length === 1 ? 'product' : 'products'} we do not stock
        </div>
      </div>

      {/* Only once the list is long enough to need it. A search box above four
          rows is furniture. */}
      {rows.length > 8 && (
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setShowAll(false); }}
          placeholder="Search their catalog…"
          style={{
            width: '100%', marginBottom: 8, padding: '8px 10px',
            // 16px, never smaller — locked rule 12, iOS auto-zoom.
            fontSize: 16, borderRadius: 7, border: '1px solid #E0E0E0',
            boxSizing: 'border-box',
          }}
        />
      )}

      {filtered.length === 0 ? (
        <div style={{
          background: '#F5F5F3', borderRadius: 8, padding: '12px 14px',
          fontSize: 12, color: '#9A9A9A', fontStyle: 'italic',
        }}>
          Nothing in {vendor.name}’s catalog matches “{search}”.
        </div>
      ) : (
        <>
          {shown.map(r => (
            <CatalogRow
              key={r.id}
              row={r}
              isMobile={isMobile}
              canPromote={canPromote}
              onPromote={setPromoting}
            />
          ))}
          {hidden > 0 && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                width: '100%', padding: '8px 12px', background: '#F5F5F3',
                border: '1px solid #E8E8E8', borderRadius: 7,
                fontSize: 12, fontWeight: 600, color: '#5A5A5A', cursor: 'pointer',
              }}
            >
              Show {hidden} more
            </button>
          )}
        </>
      )}

      {promoting && (
        <CatalogPromotionModal
          provisionalVendorItem={promoting}
          profile={auth.profile}
          onClose={() => setPromoting(null)}
          onPromoted={() => { setPromoting(null); if (onChanged) onChanged(); }}
        />
      )}
    </div>
  );
}
